import i18n from '@/locale'
import Vue from 'vue'
import router from '@/router'
import store from '@/store'
import { successCodes, ignoreCodes, loginBackInCodes, cacheWhiteList, speechApiLists } from '@/config'
import { AxiosError } from 'axios'
import { HttpStatusCode } from '@/constants'
import Speech from 'speak-tts'
import { AppVersionStorage, LocaleStorage } from '@/utils/storage'
import { MessageBox } from 'element-ui'

/**
 * 关闭loading
 * @param {*} config
 */
function closeLoading(config) {
  if (config?.loading) {
    setTimeout(() => {
      if (config?.loadingInstance) {
        config.loadingInstance.close()
        config.loadingInstance = null
      }
    })
  }
}

// 语音播报初始化
function handleSpeechInit() {
  return new Promise((resolve, reject) => {
    let speech = new Speech()
    if (!speech?.browserSupport)
      return Vue.prototype.$mgMessage(i18n.t('该浏览器暂不支持语音播报, 建议使用【谷歌】浏览器'), 'error')
    speech.setLanguage(LocaleStorage.get())
    speech
      .init()
      .then(() => {
        resolve(speech)
      })
      .catch((e) => {
        reject(e)
      })
  })
}
// 语音播报
function handleErrorVoiceSpeak(msg) {
  if (!msg) return
  handleSpeechInit().then((res) => {
    res.speak({ text: msg }).then(() => {})
  })
}
/**
 * 检查版本是否需要更新
 * @param {*} response
 */
export function checkAppVersion(response) {
  // 过滤不需要检查版本号的接口
  if (['/mg/drp-admin-api/portal/page'].includes(response.config.url)) return
  const appVersion = response.headers['app-version']
  if (appVersion) {
    const localAppVersion = AppVersionStorage.get()
    if (!localAppVersion) {
      AppVersionStorage.set(appVersion)
      return
    }
    if (localAppVersion != appVersion) {
      alert(i18n.t('检测到当前系统版本已更新，请刷新页面！'))
      AppVersionStorage.set(appVersion)
      window.location.reload()
    }
  }
}

/**
 * 状态码为 2xx 范围内都会触发此方法
 * @param {*} response
 * @returns
 */
export async function handleResponseSuccess(response) {
  const { data, config, headers } = response
  const { code } = data

  // 关闭 loading
  closeLoading(config)

  checkAppVersion(response)

  // 处理文件流
  if (config.responseType === 'blob') {
    // 2026-07-31 导出中心按 dst 统一导出结构改造：
    // 旧的 content-disposition 判断逻辑已弃用，改为解析 blob 内容区分同步导出与异步导出。
    // 同步导出返回 Excel 二进制流，直接返回原始 response；
    // 异步导出返回 JSON { code: 200, flag: 'downloadCenter' }，解析后返回该对象，
    // 保持现有业务页面中 `data.code === 200 && data.flag === 'downloadCenter'` 的分支判断兼容。
    // 旧逻辑保留注释如下，便于追溯：
    // if (Object.keys(headers).includes('content-disposition')) {
    //   return response
    // } else {
    //   return { code: 200, flag: 'downloadCenter' }
    // }
    const blob = data
    const firstChar = (await blob.slice(0, 1).text()).trim()
    if (firstChar === '{' || firstChar === '[') {
      try {
        const body = JSON.parse(await blob.text())
        if (body.code === 200 && body.flag === 'downloadCenter') {
          return body
        }
      } catch (e) {
        // 非标准 JSON，按同步导出文件流处理
      }
    }
    return response
  }

  // 处理接口正常返回数据
  if (successCodes.includes(code)) {
    if (cacheWhiteList?.includes(config.url)) {
      LocalForage.setItem(config.url, data.data)
    }
    if (headers?.['api-query-verison']) return { ...data, headers }
    return data
  }
  // 处理其他异常情况，使用自定义错误码
  response.status = HttpStatusCode.NO_SUCCESS
  return handleResponseError({ response })
}
/**
 * blob 错误响应体解析为 JSON（导出等 responseType: 'blob' 场景）
 * @param {*} data
 * @returns {Promise<object|null>}
 */
async function parseBlobErrorData(data) {
  if (!(data instanceof Blob) || !data.size) return null
  try {
    const firstChar = (await data.slice(0, 1).text()).trim()
    if (firstChar !== '{' && firstChar !== '[') return null
    return JSON.parse(await data.text())
  } catch (e) {
    return null
  }
}

/**
 * 状态码超出 2xx 范围会触发此方法
 *  status http状态码
 *  code   接口返回的特定状态码
 */
export async function handleResponseError({ code: cancelCode, message, response }) {
  if (!response) {
    response = { data: { code: cancelCode, msg: message } }
  }
  // 关闭 loading
  closeLoading(response.config || { loading: true })

  // 处理取消重复请求的错误
  if (cancelCode === AxiosError.ERR_CANCELED) {
    return Promise.reject({ code: cancelCode, msg: message, url: response?.config?.url })
  }
  // 处理客户端请求404错误
  let { status, data, statusText, config, headers } = response
  if (status === HttpStatusCode.NOT_FOUND) {
    data = { code: status, msg: `${statusText}【 ${config.url} 】` }
  } else if (config?.responseType === 'blob' || data instanceof Blob) {
    // blob 场景下 data 为 Blob，需解析后才能取到后台 msg
    const parsed = await parseBlobErrorData(data)
    if (parsed) {
      data = parsed
      response.data = parsed
    }
  }
  const { code, msg = statusText } = data || { code: 500, msg: i18n.t('网络可能存在波动，请重试!') }

  switch (status) {
    // 无权限/登录失效
    case HttpStatusCode.UNAUTHORIZED:
    case HttpStatusCode.PAYMENT_REQUIRED:
      Vue.prototype.$mgMessage(msg, 'error')
      router.push({ name: '401' })
      break
    // 处理 handleResponseSuccess 中状态码不在successCodes中的情况
    case HttpStatusCode.NO_SUCCESS:
      // 根据指定的code，忽略错误消息提醒
      if (ignoreCodes.includes(code)) return
      break
    default:
      {
        if (ignoreCodes.includes(code)) return Promise.reject(response)
        let content = msg
        // if (status >= HttpStatusCode.ERROR_RESPONSE && status < HttpStatusCode.CACHE_SUCCESS) {
        if (status >= HttpStatusCode.ERROR_RESPONSE_400){
          content = msg + `<br/> ${i18n.t('错误ID')}： ${headers['x-rid'] ? headers['x-rid'].split(',')[0] : ''}`
        }
        Vue.prototype.$mgConfirm(content, i18n.t('系统提示'), {
          dangerouslyUseHTMLString: true,
          showCancelButton: false,
          type: 'error',
          beforeClose(action, instance, done) {
            if (loginBackInCodes.includes(code)) {
              MessageBox.close() // 清除所有弹框
              store.dispatch('user/logout').then(() => {
                router.push({ name: 'login' })
              })
            }
            done()
          },
        })
      }
      return Promise.reject(response)
  }
}

/**
 * 失败响应拦截 -- 用于语音播报
 */
export function handleSpeechResponseError(response) {
  const { data, config } = response
  // 特定接口语音播报
  if (speechApiLists?.includes(config?.url)) handleErrorVoiceSpeak(data?.msg || '')
  return Promise.reject(response)
}
